import {
  SerializedAccountTrackerData,
  SerializedFile,
  SerializedPublicAccount,
  SerializedPublicAccountTrackerType,
  SerializedSmartLinkVersionTheme,
  SerializedWidget,
} from 'fym-shared/src/smartlinks/types';
import { JWTUser } from './userTypes';

// Transitional shim — the public SmartLink payload contract moved to fym-shared
// so server, musicapi and the sml-preview website share one definition.
// Remove no sooner than 2026-08-22, once long-running branches have rebased.
export type {
  SerializedFile,
  SerializedAccountTrackerData,
  SerializedPublicAccountTracker,
  SerializedPublicAccount,
  SerializedSocialLink,
  SerializedWidgetData,
  SerializedInfoWidget,
  SerializedImageWidget,
  SerializedLinkWidget,
  SerializedSocialLinksWidget,
  SerializedPlayerWidget,
  SerializedProfileWidget,
  SerializedPresaveWidget,
  SerializedMediaWidget,
  PresaveWidgetData,
  SerializedWidget,
  SerializedPublicSmartLinkVersion,
  SerializedPublicSmartLinkPublished,
  SerializedPublicSmartLinkPreview,
  SerializedPublicSmartLink,
  PublicAccountSmartLinkAPIResponse,
} from 'fym-shared/src/smartlinks/types';
export { SerializedSmartLinkVersionTheme, SerializedPublicAccountTrackerType } from 'fym-shared/src/smartlinks/types';

type YoutubeItem = { videoId: string };

export type YouTubeData = Record<string, YoutubeItem>;

export type SerializedAccountTracker = {
  id: number;
  type: SerializedPublicAccountTrackerType;
  data: SerializedAccountTrackerData;
  snippet: string | null;
  trackerId: string;
  privateData: Record<string, string>;
  /** ISO date. The trackers screen says when each one was added. */
  createdAt: string;
};

export type SerializedUser = JWTUser;

export interface SerializedAccount extends SerializedPublicAccount {
  /** True for the read-only demo artist (LaziiBee). Its answers are built in memory, never stored. */
  isDemo?: boolean;
}

export type SerializedAccountForList = {
  id: number;
  name: string | null;
  avatarFile: SerializedFile | null;
  avatarProcessedUrl: string | null;
  shareUrl: string | null;
  slug: string | null | undefined;
  /** True for the read-only demo artist (LaziiBee). It does not count towards the plan's artists. */
  isDemo?: boolean;
  /**
   * True for an artist above the plan's artist limit (a plan went down). The app shows it but
   * offers no change; a write answers 403 `M24_LIMIT_ARTIST_READ_ONLY`. The demo artist is
   * read-only too.
   */
  readOnly?: boolean;
};

export enum SerializedSmartLinkStatus {
  draft = 'draft',
  published = 'published',
}

export type SerializedSmartLink = {
  id: number;
  recentSmartLinkVersion: {
    id: number;
    name: string;
    slug: string;
    backgroundFile: SerializedFile | null;
    backgroundProcessedUrl: string;
    widgets: SerializedWidget[] | null;
    previewUrl: string | null;
    editUrl: string | null;
    status: SerializedSmartLinkStatus;
    theme: SerializedSmartLinkVersionTheme;
  };
  publishedSmartLinkVersion: {
    id: number;
    name: string;
    slug: string;
    backgroundFile: SerializedFile | null;
    backgroundProcessedUrl: string;
    widgets: SerializedWidget[] | null;
    status: SerializedSmartLinkStatus;
    publishedAt: number | null;
    theme: SerializedSmartLinkVersionTheme;
  } | null;
  accountId: number;
  shareUrl: string | null;
  /**
   * The share url on the platform domain, never on the account's custom domain. The editor
   * frames the published page with it: the web app's CSP cannot list an artist's own domain.
   * Optional because older API builds do not send it.
   */
  platformShareUrl?: string | null;
  embedUrl: string | null;
  /**
   * This page's own platform host, or null when it takes the account's. The
   * urls above are already built from the resolved one; this is what a picker
   * needs to show "same as account" rather than a host.
   */
  platformDomain: string | null;
  isMainSmartLink: boolean;
  /** Epoch millis, for the "created ..." line under the title. */
  createdAt: number;
  /**
   * How many changes of the draft are not live (the editor top bar: "Live · 2 changes not live").
   * The count of blocks that the draft adds, removes or changes (data, hidden flag) against the
   * published version, plus 1 when the block order changed, plus 1 for each changed page field
   * (name, address, theme, background). 0 when the draft equals the live page or the recent
   * version is the published one. Null for a page that was never published. Absent on an older
   * server (the editor then says "changes not live" with no number). Rule: fym-shared
   * `smartlinks/pendingChanges.ts`.
   */
  pendingChanges?: number | null;
};

export type SerializedSmartLinksList = {
  id: number;
  recentSmartLinkVersion: {
    id: number;
    name: string;
    slug: string;
    status: SerializedSmartLinkStatus;
    imagePreviewURL: string | null;
    /** The page itself, for the live preview on a card. Carries the preview
        token while the version is a draft. */
    previewURL: string | null;
  };
  publishedSmartLinkVersion: {
    id: number;
    name: string;
    slug: string;
    status: SerializedSmartLinkStatus;
  } | null;
}[];

export type SerializedSmartLinksListResponse = {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
  results: SerializedSmartLinksList;
};

export type SerializedAccountsListResponse = {
  accounts: SerializedAccountForList[];
};
